import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { getApiUrl } from '../lib/api';
import { springs } from '../lib/animations';
import AnimatedButton from '../components/AnimatedButton';
import TypewriterText from '../components/TypewriterText';

interface BattleResult {
  joint1_score: number;
  joint2_score: number;
  winner: number;
  battle_verdict: string;
}

export default function Battle() {
  const [img1, setImg1] = useState<string | null>(null);
  const [img2, setImg2] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BattleResult | null>(null);
  const [error, setError] = useState('');
  const file1 = useRef<HTMLInputElement>(null);
  const file2 = useRef<HTMLInputElement>(null);

  const handleFile = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setter(reader.result as string);
    reader.readAsDataURL(f);
  };

  const handleBattle = async () => {
    if (!img1 || !img2) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(getApiUrl('/api/battle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image1Base64: img1, image2Base64: img2 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Battle failed');
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setImg1(null);
    setImg2(null);
    setResult(null);
    setError('');
    if (file1.current) file1.current.value = '';
    if (file2.current) file2.current.value = '';
  };

  const JointSlot = ({ img, fileRef, onFile, label, score, isWinner }: {
    img: string | null; fileRef: React.RefObject<HTMLInputElement>; onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
    label: string; score?: number; isWinner?: boolean;
  }) => (
    <div className="flex-1">
      <div className="text-center text-cream/60 text-sm font-semibold mb-2">{label}</div>
      <div
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition min-h-[200px] flex items-center justify-center ${
          result && isWinner ? 'border-cannabis-400 bg-cannabis-800/50' : result && !isWinner ? 'border-red-700/50 bg-red-900/10' : 'border-cannabis-600 hover:border-cannabis-400'
        }`}
        onClick={() => !result && fileRef.current?.click()}
      >
        {img ? (
          <div className="relative">
            <img src={img} alt={label} className="max-h-48 mx-auto rounded-lg" />
            {result && (
              <motion.div
                className="absolute -top-3 -right-3 text-3xl"
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={isWinner ? springs.bouncy : springs.subtle}
              >
                {isWinner ? 'Winner' : ''}
              </motion.div>
            )}
          </div>
        ) : (
          <div className="text-cream/30 text-sm">Upload</div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
      </div>
      {score !== undefined && (
        <div className={`text-center mt-3 text-2xl font-bold ${isWinner ? 'text-cannabis-300' : 'text-red-400'}`}>
          {score.toFixed(1)}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold text-cream mb-1 text-center">Roast Battle</h2>
      <p className="text-cream/50 text-sm mb-6 text-center">Two joints enter. One gets roasted.</p>

      <div className="flex gap-4 mb-6">
        <JointSlot
          img={img1} fileRef={file1 as any} onFile={handleFile(setImg1)} label="Joint #1"
          score={result?.joint1_score} isWinner={result?.winner === 1}
        />
        <motion.div
          className="flex items-center text-3xl text-cream/30 font-bold"
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          style={{ textShadow: '0 0 20px rgba(212,165,116,0.3)' }}
        >
          VS
        </motion.div>
        <JointSlot
          img={img2} fileRef={file2 as any} onFile={handleFile(setImg2)} label="Joint #2"
          score={result?.joint2_score} isWinner={result?.winner === 2}
        />
      </div>

      {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}

      {result ? (
        <motion.div className="space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={springs.subtle}>
          <div className="bg-cannabis-800 rounded-xl p-5 border border-cannabis-700">
            <p className="text-cream/90 italic text-lg leading-relaxed text-center">"<TypewriterText text={result.battle_verdict} />"</p>
          </div>
          <AnimatedButton onClick={reset} variant="secondary" className="w-full">
            Battle Again
          </AnimatedButton>
        </motion.div>
      ) : (
        <AnimatedButton
          onClick={handleBattle}
          disabled={!img1 || !img2}
          loading={loading}
          className="w-full"
        >
          {loading ? 'The judge is deliberating...' : 'Start Battle'}
        </AnimatedButton>
      )}
    </div>
  );
}
